
import { createClient } from '@supabase/supabase-js';
import type { Database } from './types';

// Env-driven so the local perf stack / staging can be targeted without a code change.
// Falls back to prod when VITE_SUPABASE_URL is unset (normal builds). Must end with "/".
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "https://api.coriscofrila.com.br/";
const SUPABASE_PUBLISHABLE_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImR0a3VmaWRoZGtvbnhwZHN3cXJzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzc4MTU0MTUsImV4cCI6MjA1MzM5MTQxNX0.4A8Mhk62-BJHT2uB9aioyAmzttzX00kmGwBJmWGcOzo";

export { SUPABASE_URL };

// Import the supabase client like this:
// import { supabase } from "@/integrations/supabase/client";

export const supabase = createClient<Database>(
  SUPABASE_URL,
  SUPABASE_PUBLISHABLE_KEY,
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
    },
    db: {
      schema: 'public',
    },
    global: {
      headers: {
        'x-application-name': 'corisco-frilas',
      },
    },
  }
);

// Type for table names in our database
type TableName = keyof Database['public']['Tables'];

// Simple interface for query options to avoid excessive type recursion
interface QueryOptions {
  filters?: Record<string, unknown>;
  pageSize?: number;
  orderBy?: {
    column: string;
    ascending: boolean;
  };
}

// Função utilitária para buscar todos os registros de uma tabela, paginando automaticamente
export async function fetchAllRecords<T>(
  table: TableName, 
  columns: string = '*', 
  options: QueryOptions = {}
): Promise<T[]> {
  const { 
    filters = {},
    pageSize = 1000,
    orderBy = { column: 'created_at', ascending: false }
  } = options;
  
  let allRecords: T[] = [];
  let page = 0;
  let hasMore = true;
  
  while (hasMore) {
    const from = page * pageSize;
    const to = from + pageSize - 1;
    
    // Type cast to any to avoid circular type references
    let query = supabase
      .from(table)
      .select(columns)
      .range(from, to)
      .order(orderBy.column, { ascending: orderBy.ascending });
      
    // Aplicando filtros, se houver
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (typeof value === 'string' && value.trim() === '') return;
        if (Array.isArray(value) && value.length === 0) return;
        // Using any here to avoid TypeScript issues with dynamic filtering
        (query as any) = (query as any).eq(key, value);
      }
    });

    const { data, error } = await query;
    
    if (error) {
      console.error(`Erro ao buscar registros da tabela ${table}:`, error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      hasMore = false;
    } else {
      allRecords = [...allRecords, ...(data as T[])];
      page++;
      
      // Se retornou menos registros que o pageSize, não há mais páginas
      if (data.length < pageSize) {
        hasMore = false;
      }
    }
  }
  
  console.log(`Total de registros carregados da tabela ${table}:`, allRecords.length);
  return allRecords;
}
